import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, type ListResponse } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';

type CalendarEventsInput = {
  pastOrUpcoming: 'PAST' | 'UPCOMING';
  clientMeetingsOnly?: boolean;
  clientId?: string;
  clientMeetingsAndWithRecordingOnly?: boolean;
};

export const useCalendarEvents = ({
  pastOrUpcoming,
  clientMeetingsOnly = undefined,
  clientMeetingsAndWithRecordingOnly = false,
  clientId,
}: CalendarEventsInput) => {
  const startOfMinute = new Date();
  startOfMinute.setSeconds(0, 0);

  const resource = clientId ? `clients/${clientId}/calendar-events` : 'calendar-events';
  const params = useMemo(
    () =>
      buildParams(
        pastOrUpcoming,
        startOfMinute,
        clientMeetingsOnly,
        clientId,
        clientMeetingsAndWithRecordingOnly,
      ),
    [pastOrUpcoming, startOfMinute, clientMeetingsOnly, clientId],
  );

  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery<
      ListResponse<CalendarEvent>,
      Error,
      InfiniteData<ListResponse<CalendarEvent>>,
      readonly unknown[],
      number
    >({
      queryKey: [resource, params],
      queryFn: ({ pageParam }) =>
        api.getPaginated<CalendarEvent>(resource, {
          page: pageParam,
          size: 10,
          ...params,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
        return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
      },
    });

  const events = data?.pages.flatMap(page => page.items) ?? [];

  return {
    events,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

function buildParams(
  pastOrUpcoming: 'PAST' | 'UPCOMING',
  date: Date,
  clientMeetingsOnly: boolean | undefined,
  clientId?: string,
  clientMeetingsAndWithRecordingOnly?: boolean,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    order: pastOrUpcoming === 'PAST' ? 'desc' : 'asc',
  };

  if (pastOrUpcoming === 'PAST') {
    params.endAt_lt = date.toISOString();
  } else {
    params.endAt_gte = date.toISOString();
  }

  if (!clientId && clientMeetingsOnly !== undefined) {
    params.clientMeetingsOnly = clientMeetingsOnly;
  }

  if (clientMeetingsAndWithRecordingOnly) {
    params.clientMeetingsAndWithRecordingOnly = true;
  }

  return params;
}
